import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { login as authLogin, logout as authLogout, restoreSession, hasPermission, isSessionValid, type Permission } from "../services/auth";
import type { AuthSession, PortalRole } from "../types";

interface AuthContextValue {
  session: AuthSession | null;
  loading: boolean;
  error: string | null;
  notification: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  can: (permission: Permission) => boolean;
  role: PortalRole | null;
  clearNotification: () => void;
}

const STORAGE_KEY = "devportal-auth-session";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);

  // Restore session on mount
  useEffect(() => {
    const restored = restoreSession();
    if (restored) {
      setSession(restored);
    }
    setLoading(false);
  }, []);

  // Periodically check session validity + role changes
  useEffect(() => {
    if (!session) return;
    const check = async () => {
      try {
        const result = await isSessionValid(session.username);

        if (!result.valid) {
          authLogout();
          setSession(null);
          setError("Your account has been removed. Please contact an admin.");
          return;
        }

        // Detect role change
        if (result.role && result.role !== session.role) {
          const updatedSession: AuthSession = {
            ...session,
            role: result.role as PortalRole,
          };
          setSession(updatedSession);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
          setNotification(`Your role has been changed to ${result.role.charAt(0).toUpperCase() + result.role.slice(1)}. Permissions updated.`);
        }
      } catch {
        // network error, don't log out
      }
    };
    const id = setInterval(check, 15000); // check every 15s
    return () => clearInterval(id);
  }, [session]);

  const handleLogin = useCallback(async (username: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const sess = await authLogin(username, password);
      setSession(sess);
    } catch (err: any) {
      const msg = err.message || "Authentication failed";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogout = useCallback(() => {
    authLogout();
    setSession(null);
  }, []);

  const can = useCallback(
    (permission: Permission): boolean => {
      if (!session) return false;
      return hasPermission(session.role, permission);
    },
    [session]
  );

  const clearNotification = useCallback(() => setNotification(null), []);

  return (
    <AuthContext.Provider
      value={{
        session,
        loading,
        error,
        notification,
        login: handleLogin,
        logout: handleLogout,
        can,
        role: session?.role || null,
        clearNotification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
